/*
 * @Author: Ryan
 * @Date: 2025-12-25
 * @Description: Start button component with gradient style for AI Avatar Generator
 */
import Link from 'next/link';
import React from 'react';

import { scrollToTop } from '@/utils/landing/scrollToTop';

import styles from './StartButton.module.scss';

export interface StartButtonProps {
  /**
   * 按钮文本
   */
  text?: string;
  /**
   * 跳转链接，如果提供则渲染为 a 标签
   */
  href?: string;
  /**
   * 自定义点击处理函数（仅在无 href 时生效）
   */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * 自定义类名
   */
  className?: string;
  /**
   * 自定义样式
   */
  style?: React.CSSProperties;
  /**
   * GTM element-name 属性值，用于 GTM 通过 CSS 选择器 [element-name] 捕获点击事件
   */
  elementName?: string;
}

const StartButton: React.FC<StartButtonProps> = ({
  text = 'Create Your AI Avatar Video',
  href,
  onClick,
  className,
  style,
  elementName,
}) => {
  const buttonClassName = `${styles.button} ${className || ''}`.trim();

  // 如果有 href，使用 Link + a 标签（SEO 友好）
  if (href) {
    return (
      <Link
        href={href}
        className={buttonClassName}
        style={style}
        {...(elementName ? { 'element-name': elementName } : {})}
      >
        {text}
      </Link>
    );
  }

  // 否则使用 button 标签
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(e);
    } else {
      scrollToTop();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={buttonClassName}
      style={style}
      {...(elementName ? { 'element-name': elementName } : {})}
    >
      {text}
    </button>
  );
};

export default StartButton;
